import { useEffect, useMemo, useState } from 'react';
import { CheckCircle2, PackageCheck, Save } from 'lucide-react';

function QuantityControl({ item, value, onChange, disabled }) {
  const requiredQuantity = Math.max(0, Number(item.requiredQuantity) || 0);
  const itemName = item.name || 'item';
  if (requiredQuantity === 1) {
    return (
      <label className="inline-flex min-h-11 cursor-pointer items-center gap-2 rounded-xl border border-neutral-200 px-3 dark:border-neutral-700">
        <input type="checkbox" aria-label={`Packed ${itemName}`} checked={value === 1} onChange={event => onChange(event.target.checked ? 1 : 0)} disabled={disabled} className="h-4 w-4 accent-accent" />
        <span className="text-sm font-medium">Packed</span>
      </label>
    );
  }
  return (
    <label className="block text-xs font-medium text-neutral-500 dark:text-neutral-400">
      Packed quantity <span className="sr-only">for {itemName}</span>
      <input
        aria-label={`Packed quantity for ${itemName}`}
        type="number"
        inputMode="numeric"
        min="0"
        max={requiredQuantity}
        value={value}
        onChange={event => onChange(Math.min(requiredQuantity, Math.max(0, Number(event.target.value) || 0)))}
        disabled={disabled}
        className="mt-1 min-h-11 w-full rounded-xl border border-neutral-200 bg-white px-3 text-sm text-neutral-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus dark:border-neutral-700 dark:bg-neutral-900 dark:text-white"
      />
      <span className="mt-1 block">of {requiredQuantity}</span>
    </label>
  );
}

export default function PackingChecklistEditor({ order, busy, onSave, onAction, onDirtyChange }) {
  const [quantities, setQuantities] = useState({});
  const items = useMemo(() => order?.items || [], [order]);

  useEffect(() => {
    setQuantities(Object.fromEntries(items.map(item => [item.orderItemId, Number(item.packedQuantity) || 0])));
  }, [items]);

  const complete = items.length > 0 && items.every(item => Number(quantities[item.orderItemId]) === Number(item.requiredQuantity));
  const changed = items.some(item => Number(quantities[item.orderItemId]) !== Number(item.packedQuantity));
  const totalPacked = items.reduce((sum, item) => sum + Number(quantities[item.orderItemId] || 0), 0);
  const totalRequired = items.reduce((sum, item) => sum + (Number(item.requiredQuantity) || 0), 0);
  const started = order.fulfillmentStatus === 'PROCESSING';
  const packed = ['PACKED', 'READY_FOR_DISPATCH'].includes(order.fulfillmentStatus);
  const editable = started && order.capabilities?.update;
  const canStart = order.nextAction === 'START_PACKING' && order.capabilities?.update;

  useEffect(() => { onDirtyChange?.(changed); }, [changed, onDirtyChange]);

  const checklist = () => items.map(item => ({
    orderItemId: item.orderItemId,
    packedQuantity: Number(quantities[item.orderItemId] || 0),
    expectedVersion: item.version,
  }));
  const save = () => onSave(checklist());
  // Marking the parcel packed saves the ticked quantities itself; "Save
  // progress" is only for stopping part-way (F8, 2026-09-26).
  const markPacked = () => onAction('MARK_PACKED', changed ? checklist() : null);

  return (
    <section aria-labelledby="packing-task-title" className="rounded-2xl border border-neutral-200 p-4 dark:border-neutral-800">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex gap-3">
          <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-accent text-sm font-semibold text-on-accent">1</span>
          <div><h3 id="packing-task-title" className="font-semibold">Pack order</h3><p className="mt-1 text-sm leading-5 text-neutral-500">Confirm the physical quantity for every order line before locking the parcel as packed.</p></div>
        </div>
        <span className="self-start rounded-full bg-neutral-100 px-3 py-1 text-xs font-semibold tabular-nums text-neutral-600 dark:bg-neutral-900 dark:text-neutral-300">{totalPacked} / {totalRequired} packed</span>
      </div>

      {packed ? (
        <div className="mt-4 flex gap-3 rounded-xl border border-success bg-success-subtle p-4 text-sm text-success">
          <CheckCircle2 className="h-5 w-5 shrink-0" aria-hidden="true" />
          <div><p className="font-semibold">Packing complete</p><p className="mt-1">Packed quantities are locked. Continue to Label &amp; Submit for Dispatch.</p></div>
        </div>
      ) : null}

      {canStart ? (
        <div className="mt-4 rounded-xl border border-info bg-info-subtle p-4 text-sm text-info">
          <p className="font-semibold">Start packing to edit quantities</p>
          <p className="mt-1">This records that preparation has begun; it does not reserve inventory or change payment.</p>
        </div>
      ) : null}

      <div className="mt-4 space-y-3">
        {items.map(item => (
          <article key={item.orderItemId} className="flex flex-col gap-4 rounded-xl border border-neutral-200 p-4 dark:border-neutral-800 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0"><p className="break-words font-medium">{item.name || 'Item unavailable'}</p><p className="mt-1 text-xs text-neutral-500">{item.variation || 'Standard variant'} · Required {Number(item.requiredQuantity) || 0}</p></div>
            <div className="w-full shrink-0 sm:w-44"><QuantityControl item={item} value={Number(quantities[item.orderItemId] || 0)} onChange={value => setQuantities(current => ({ ...current, [item.orderItemId]: value }))} disabled={!editable || busy} /></div>
          </article>
        ))}
      </div>

      {!order.capabilities?.update ? <p className="mt-4 rounded-xl bg-neutral-100 p-3 text-sm text-neutral-600 dark:bg-neutral-900 dark:text-neutral-300">You have read-only access to packing.</p> : null}

      {canStart ? (
        <button type="button" disabled={busy} onClick={() => onAction('START_PACKING')} className="business-primary-action mt-4 inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-xl bg-accent px-4 text-sm font-semibold text-on-accent hover:bg-accent-hover active:bg-accent-active focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus disabled:opacity-45">
          <PackageCheck className="h-4 w-4" aria-hidden="true" />{busy ? 'Starting…' : 'Start packing'}
        </button>
      ) : null}

      {editable ? (
        <div className="mt-4 grid gap-2 sm:grid-cols-2">
          <button type="button" disabled={!changed || busy} onClick={save} className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl border border-neutral-300 px-4 text-sm font-semibold hover:bg-neutral-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus disabled:cursor-not-allowed disabled:opacity-45 dark:border-neutral-700 dark:hover:bg-neutral-900">
            <Save className="h-4 w-4" aria-hidden="true" />{busy ? 'Saving…' : 'Save progress'}
          </button>
          <button type="button" disabled={busy || !complete} onClick={markPacked} className="business-primary-action inline-flex min-h-11 items-center justify-center gap-2 rounded-xl bg-accent px-4 text-sm font-semibold text-on-accent hover:bg-accent-hover active:bg-accent-active focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus disabled:cursor-not-allowed disabled:opacity-45">
            <PackageCheck className="h-4 w-4" aria-hidden="true" />{busy ? 'Saving…' : 'Mark parcel packed'}
          </button>
        </div>
      ) : null}
      {editable && !complete ? <p className="mt-2 text-xs text-neutral-500">Tick every ordered unit to mark the parcel packed. Stopping part-way? Save progress keeps what you have ticked.</p> : null}
    </section>
  );
}
